import { createStorage } from '@aviata/storage';
import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router';

export interface UtmParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_keyword?: string;
  utm_content?: string;
  utm_term?: string;
  gclid?: string;
}

interface StoredUtmData {
  params: UtmParams;
  timestamp: number;
}

const UTM_STORAGE_KEY = 'tour_utm_params';

export const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_keyword',
  'utm_content',
  'utm_term',
  'gclid',
] as const;

export function extractUtmFromUrl(): UtmParams {
  const url = new URL(window.location.href);
  const params: UtmParams = {};

  UTM_KEYS.forEach(key => {
    const value = url.searchParams.get(key);
    if (value) {
      params[key as keyof UtmParams] = value;
    }
  });

  return params;
}

export function saveUtmParams(params: UtmParams): void {
  if (Object.keys(params).length === 0) return;

  const storage = createStorage(sessionStorage);
  const data: StoredUtmData = {
    params,
    timestamp: Date.now(),
  };

  storage.stringify(UTM_STORAGE_KEY, data);
}

export function getUtmParams(): UtmParams {
  const urlParams = extractUtmFromUrl();

  if (Object.keys(urlParams).length > 0) {
    saveUtmParams(urlParams);
    return urlParams;
  }

  try {
    const storage = createStorage(sessionStorage);
    const storedData = storage.parse(UTM_STORAGE_KEY) as StoredUtmData;

    if (!storedData) return {};

    return storedData.params;
  } catch (error) {
    clearUtmParams();
    return {};
  }
}

export function clearUtmParams(): void {
  const storage = createStorage(sessionStorage);
  storage.remove(UTM_STORAGE_KEY);
}

export const utmRouterGuard = (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext,
) => {
  const utmParams = extractUtmFromUrl();

  if (Object.keys(utmParams).length > 0) {
    saveUtmParams(utmParams);
  }

  next();
};
